/* eslint-disable */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import type { DeliverySummary } from '../../types/productDashboard';

interface ProgressIndicatorProps {
  deliverySummary: DeliverySummary;
  showBreakdown?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  deliverySummary,
  showBreakdown = true,
  size = 'medium',
}) => {
  const {
    total_stories,
    completed_stories,
    in_progress_stories,
    backlog_stories,
    completion_percentage,
    total_tasks,
    completed_tasks,
  } = deliverySummary;

  const totalItems = total_stories + total_tasks;
  const completedItems = completed_stories + completed_tasks;
  const inProgressItems = in_progress_stories;
  const backlogItems = backlog_stories;

  // Calculate percentages for each segment
  const completedPercent = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const inProgressPercent = totalItems > 0 ? (inProgressItems / totalItems) * 100 : 0;
  const backlogPercent = totalItems > 0 ? (backlogItems / totalItems) * 100 : 0;

  const height = size === 'small' ? 8 : size === 'medium' ? 12 : 16;
  const fontSize = size === 'small' ? '0.7rem' : size === 'medium' ? '0.75rem' : '0.875rem';

  const tooltipContent = (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Delivery Progress
      </Typography>
      <Typography variant="caption" display="block">
        Completed: {completedItems} ({completedPercent.toFixed(1)}%)
      </Typography>
      <Typography variant="caption" display="block">
        In Progress: {inProgressItems} ({inProgressPercent.toFixed(1)}%)
      </Typography>
      <Typography variant="caption" display="block">
        Backlog: {backlogItems} ({backlogPercent.toFixed(1)}%)
      </Typography>
      {total_tasks > 0 && (
        <>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Stories: {completed_stories}/{total_stories} completed
          </Typography>
          <Typography variant="caption" display="block">
            Tasks: {completed_tasks}/{total_tasks} completed
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box
        sx={{
          width: '100%',
          minWidth: '100px',
        }}
        role="progressbar"
        aria-valuenow={completion_percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Delivery progress: ${completion_percentage.toFixed(0)}% complete`}
      >
        {/* Progress bar with color-coded segments */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: `${height}px`,
            backgroundColor: 'grey.200',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {/* Completed segment (green) */}
          {completedPercent > 0 && (
            <Box
              sx={{
                width: `${completedPercent}%`,
                backgroundColor: 'success.main',
                height: '100%',
                transition: 'width 0.3s ease-in-out',
              }}
              aria-label={`Completed: ${completedItems} items`}
            />
          )}
          {/* In Progress segment (blue) */}
          {inProgressPercent > 0 && (
            <Box
              sx={{
                width: `${inProgressPercent}%`,
                backgroundColor: 'primary.main',
                height: '100%',
                transition: 'width 0.3s ease-in-out',
              }}
              aria-label={`In Progress: ${inProgressItems} items`}
            />
          )}
          {/* Backlog segment (gray) */}
          {backlogPercent > 0 && (
            <Box
              sx={{
                width: `${backlogPercent}%`,
                backgroundColor: 'grey.400',
                height: '100%',
                transition: 'width 0.3s ease-in-out',
              }}
              aria-label={`Backlog: ${backlogItems} items`}
            />
          )}
        </Box>

        {/* Percentage label — always visible */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 0.5,
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize,
              fontWeight: 'medium',
              color: 'text.primary',
            }}
          >
            {completion_percentage.toFixed(0)}% Complete
          </Typography>
          {/* Breakdown numbers — conditional */}
          {showBreakdown && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize, color: 'text.secondary' }}>
                  {completedItems}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize, color: 'text.secondary' }}>
                  {inProgressItems}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: 'grey.400',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize, color: 'text.secondary' }}>
                  {backlogItems}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};
